import { useContext, useMemo } from "react";
import { MdContentCopy } from "react-icons/md";
import { AppContext } from "../App";
import {
  convertWgs84PointsToUtm,
  formatProjectedCoordinate,
  WGS84_GPS_CRS,
} from "../CoordinateSystems";
import { copyText } from "../Helpers";
import {
  STR_COORDINATE_COPIED,
  STR_COORDINATE_FORMATS,
  STR_COPY,
  STR_COPY_FAILED,
  STR_EASTING,
  STR_EASTING_NORTHING,
  STR_LATITUDE_LONGITUDE,
  STR_MOBILE_GPS,
  STR_NORTHING,
  STR_SOURCE_CRS,
} from "../Strings";

export default function CoordinateCards({
  points = [],
  sourceCrs = WGS84_GPS_CRS,
  sourceLabel,
}) {
  const appContext = useContext(AppContext);
  const conversion = useMemo(() => {
    try {
      return convertWgs84PointsToUtm(points, sourceCrs);
    } catch (error) {
      return null;
    }
  }, [points, sourceCrs]);

  if (!conversion) {
    return null;
  }

  async function copyCoordinate(value) {
    const copied = await copyText(value);
    if (copied) {
      appContext.tellMessage(STR_COORDINATE_COPIED[appContext.language]);
    } else {
      appContext.tellError(STR_COPY_FAILED[appContext.language]);
    }
  }

  return (
    <div>
      <h6 className="text-muted mb-2">{STR_COORDINATE_FORMATS[appContext.language]}</h6>
      <p className="text-muted" style={{ fontSize: "12px" }}>
        {STR_SOURCE_CRS[appContext.language]}: {sourceCrs.name} ({sourceCrs.epsg}) ·{" "}
        {sourceLabel || STR_MOBILE_GPS[appContext.language]}
      </p>

      <div className="row">
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <h6 className="font-semi-bold mNoMargin">
                  WGS 84 · {STR_LATITUDE_LONGITUDE[appContext.language]}
                </h6>
                <span className="badge bg-dark">{sourceCrs.epsg}</span>
              </div>

              {conversion.points.map((point, index) => {
                const value = `${point.lat}, ${point.lng}`;
                return (
                  <div
                    className="d-flex justify-content-between align-items-center border-bottom py-2"
                    key={`wgs84-${point.lat}-${point.lng}-${index}`}
                  >
                    <span className="font-semi-bold me-2">#{index + 1}</span>
                    <span className="text-break mx-2">{value}</span>
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => copyCoordinate(value)}
                    >
                      <MdContentCopy className="me-1" />
                      {STR_COPY[appContext.language]}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <h6 className="font-semi-bold mNoMargin">
                  {STR_EASTING_NORTHING[appContext.language]}
                </h6>
                <span className="badge bg-primary">{conversion.targetCrs.epsg}</span>
              </div>
              <p className="text-muted" style={{ fontSize: "12px" }}>
                {conversion.targetCrs.name}
              </p>

              {conversion.points.map((point, index) => {
                const easting = formatProjectedCoordinate(point.easting);
                const northing = formatProjectedCoordinate(point.northing);
                const value = `E ${easting} m, N ${northing} m, ${conversion.targetCrs.name}, ${conversion.targetCrs.epsg}`;

                return (
                  <div
                    className="d-flex justify-content-between align-items-center border-bottom py-2"
                    key={`utm-${point.easting}-${point.northing}-${index}`}
                  >
                    <span className="font-semi-bold me-2">#{index + 1}</span>
                    <span className="mx-2">
                      {STR_EASTING[appContext.language]}: {easting} m<br />
                      {STR_NORTHING[appContext.language]}: {northing} m
                    </span>
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => copyCoordinate(value)}
                    >
                      <MdContentCopy className="me-1" />
                      {STR_COPY[appContext.language]}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
